import React, { useState, ChangeEvent, useEffect } from 'react';
import { Button, FormGroup, Form, Input, Label, Nav, NavItem, NavLink, TabContent, TabPane, Card } from 'reactstrap';
import { useAlert } from 'hooks';
import { useHistory, Link } from 'react-router-dom';
import { setCookie, deleteCookie } from 'helpers';
import AlertModal from 'components/AlertModal';
import config from 'config';
import { useUser } from 'context';
import { valid, lt } from 'semver';
import { Eye, EyeOff } from 'lucide-react';

import './Login.scss';

function Login() {
  const user = useUser();
  const [showAlert, message, alert] = useAlert(false, '');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    version: config.version,
    matriculate_id: '',
    doc: '',
    password: '',
  });

  const [loading, setLoading] = useState(true);
  const [currentPane, setCurrentPane] = useState('1');

  const history = useHistory();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${ config.baseUrl }/auth/autogestion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const payload = await res.json();

      if (!payload || !payload.success) {
        console.debug(payload);
        deleteCookie('accessToken');

        if (payload?.error?.name === 'SequelizeConnectionError') {
          alert(true, 'No se pudo autenticar la matrícula y contraseña ingresadas porque hay un problema de conexión' +
            '.Por favor, intente nuevamente más tarde');
        } else if (payload?.reason === 'MATRICULATE_INACTIVE') {
          alert(true, 'La matrícula ingresada no se encuentra activa');
        } else {
          alert(true, 'La matrícula y/o contraseña no son correctas. Por favor, verifique que los datos ingresados ' +
            'sean correctos e intente nuevamente');
        }
        setLoading(false);
        return;
      }

      user.setUser(payload.user);
      setCookie('accessToken', payload.accessToken, { days: 10 });
      setLoading(false);
      history.push('/dashboard');
    } catch (err) {
      console.error(err);
      alert(true, 'Se produjo un error al intentar validar sus datos. por favor, intente nuevamente más tarde');
      deleteCookie('accessToken');
      setLoading(false);
    }
  };

  const handleSubmitNM = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${ config.baseUrl }/auth/autogestion-nm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const payload = await res.json();

      if (!payload || !payload.success) {
        deleteCookie('accessToken');

        if (payload?.error?.name === 'SequelizeConnectionError') {
          alert(true, 'No se pudo autenticar el documento y contraseña ingresadas porque hay un problema de conexión' +
            '.Por favor, intente nuevamente más tarde');
        } else {
          alert(true, 'El documento y/o contraseña no son correctos. Por favor, verifique que los datos ingresados ' +
            'sean correctos e intente nuevamente');
        }
        setLoading(false);
        return;
      }

      user.setUser(payload.user);
      setCookie('accessToken', payload.accessToken, { days: 10 });
      setLoading(false);
      history.push('/inicio');
    } catch (err) {
      console.error(err);
      alert(true, 'Se produjo un error al intentar validar sus datos. por favor, intente nuevamente más tarde');
      deleteCookie('accessToken');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch(`${ config.baseUrl }/system/version`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(res => res.json())
      .then(res => {
        if (!res || !res.au) {
          return;
        }

        const currentVersion = valid(res.au);
        if (!currentVersion) {
          return;
        }

        const reload = lt(config.version, currentVersion);

        if (reload) {
          window.location.reload();
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="login-container">
      <AlertModal isOpen={ showAlert } message={ message } onClose={ () => alert(false) } />
      <Card style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        width: '500px',
        zIndex: 2,
        padding: '30px 45px',
      }}>
        <a href="https://www.cokiba.org.ar/" className="simple-text" target="_blank" rel="noopener noreferrer">
          <img
            src={ require('../logo-40.png') }
            alt="Logo CoKiBA"
          />
        </a>
        <div className="small text-center font-weight-bold">
          v{ config.version }
        </div>
        <br /><br />
        <Nav tabs>
          <NavItem style={{ flexGrow: 1, cursor: 'pointer' }}>
            <NavLink
              className={ currentPane == '1' ? 'active' : ''}
              onClick={ () => setCurrentPane('1') }
              style={{ color: '#9123C0' }}
            >Matriculados</NavLink>
          </NavItem>
          <NavItem style={{ flexGrow: 1, cursor: 'pointer' }}>
            <NavLink
              className={ currentPane == '2' ? 'active' : ''}
              onClick={ () => setCurrentPane('2') }
              style={{ color: '#9123C0' }}
            >No Matriculados</NavLink>
          </NavItem>
        </Nav>
        <TabContent
          activeTab={currentPane}
          style={{ padding: '16px' }}
        >
          <TabPane tabId="1">
            <Form>
              <FormGroup>
                <Label htmlFor="matriculate_id">Matrícula</Label>
                <Input
                  name="matriculate_id"
                  type="text"
                  placeholder="Ingresar el número de matrícula"
                  className="text-center"
                  onChange={ handleChange }
                  disabled={ loading }
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="password">Contraseña</Label>
                <div className="login-password-input">
                  <Input
                    name="password"
                    type={ showPassword ? 'text' : 'password' }
                    placeholder="Ingresar la contraseña"
                    className="text-center"
                    autoComplete="current-password"
                    onChange={ handleChange }
                    disabled={ loading }
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={ () => setShowPassword(!showPassword) }
                    aria-label={ showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña' }
                    aria-pressed={ showPassword }
                    disabled={ loading }
                  >
                    { showPassword ? <EyeOff size={18} /> : <Eye size={18} /> }
                  </button>
                </div>
              </FormGroup>
              <Button
                color="primary"
                onClick={ handleSubmit }
                disabled={ loading }
              >
                {
                  loading
                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    : <>Iniciar Sesión</>
                }
              </Button>
              <div>
                <Link to="/password">¿OLVIDASTE TU CONTRASEÑA?</Link>
              </div>
            </Form>
          </TabPane>
          <TabPane tabId="2">
            <Form>
              <FormGroup>
                <Label htmlFor="doc">DNI</Label>
                <Input
                  name="doc"
                  type="text"
                  placeholder="Ingresar el número de documento"
                  className="text-center"
                  onChange={ handleChange }
                  disabled={ loading }
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="password">Contraseña</Label>
                <div className="login-password-input">
                  <Input
                    name="password"
                    type={ showPassword ? 'text' : 'password' }
                    placeholder="Ingresar la contraseña"
                    className="text-center"
                    autoComplete="current-password"
                    onChange={ handleChange }
                    disabled={ loading }
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={ () => setShowPassword(!showPassword) }
                    aria-label={ showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña' }
                    aria-pressed={ showPassword }
                    disabled={ loading }
                  >
                    { showPassword ? <EyeOff size={18} /> : <Eye size={18} /> }
                  </button>
                </div>
              </FormGroup>
              <Button
                color="primary"
                onClick={ handleSubmitNM }
                disabled={ loading }
              >
                {
                  loading
                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    : <>Iniciar Sesión</>
                }
              </Button>
              <div>
                <Link to="/password-nm">¿OLVIDASTE TU CONTRASEÑA?</Link>
                <br /><br />
                <Link
                  to="/registration?mail-sent=1"
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '18px',
                    letterSpacing: '.32px',
                  }}
                >¿QUIERES VALIDAR TU CORREO?</Link>
                <br /><br />
                <Link
                  to="/registration"
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '18px',
                    letterSpacing: '.32px',
                  }}
                >¿NO ESTÁS REGISTRADO?</Link>
              </div>
            </Form>
          </TabPane>
        </TabContent>
      </Card>
    </div>
  );
}

export default Login;
